import type { FC } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useCalculatePriceForDisplayCurrency } from "../hooks";
import { useAccountsContext } from "./accounts";
import { useDexContext } from "./dex";
import { useEnrichedWallet } from "./enrichedWallet";
import { useFarms } from "./farm";
import { useLendingContext } from "./lending";
import { useLiquidity } from "./liquidity";
import { useManagedFundsContext } from "./managedFunds";
import { useMarginContext } from "./margin";
import { useMarkets } from "./market";
import { useNFTContext } from "./nft";
import { useNFTMarketsContext } from "./nftMarkets";
import { useStakingContext } from "./staking";
import { useValidatorStakingContext } from "./validorStaking";
import { useVaults } from "./vaults";

interface PortfolioOverview {
  networth: number;
  walletBalance: number;
  lpBalance: number;
  farmBalance: number;
  yieldAggBalance: number;
  NFTBalance: number;
  marginBalance: number;
  dexBalance: number;
  validatorBalance: number;
  lendingBalance: number;
  stakingBalance: number;
  managedFundsBalance: number;
  nftMarketsBalance: number;
}

const PortfolioOverviewContext = createContext<PortfolioOverview>({
  networth: 0,
  walletBalance: 0,
  lpBalance: 0,
  farmBalance: 0,
  yieldAggBalance: 0,
  NFTBalance: 0,
  marginBalance: 0,
  dexBalance: 0,
  validatorBalance: 0,
  lendingBalance: 0,
  stakingBalance: 0,
  managedFundsBalance: 0,
  nftMarketsBalance: 0,
});

export const PortfolioOverviewContextProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const { connected } = useEnrichedWallet();
  const { displayCurrency } = useMarkets();
  const [networth, setNetworth] = useState(0);

  const {
    parsedUserTokenAccounts: { userTokenAccountsBalanceUSD },
  } = useAccountsContext();
  const { marginBalance, setMarginBalance } = useMarginContext();
  const { liquidityBalance, setLiquidityBalance } = useLiquidity();
  const { farmBalance, setFarmBalance } = useFarms();
  const { vaultsBalance, setVaultsBalance } = useVaults();
  const { stakingBalance, setStakingBalance } = useStakingContext();
  const { NFTBalance, setNFTBalance } = useNFTContext();
  const { dexBalance, setDexBalance } = useDexContext();
  const { lendingBalance, setLendingBalance } = useLendingContext();
  const { validatorStakingBalance, setValidatorStakingBalance } = useValidatorStakingContext();
  const { managedFundsBalance, setManagedFundsBalance } = useManagedFundsContext();
  const { nftMarketsBalance, setNFTMarketsBalance } = useNFTMarketsContext();

  useEffect(() => {
    if (!connected) {
      setNetworth(0);
      setMarginBalance(0);
      setLiquidityBalance(0);
      setFarmBalance(0);
      setVaultsBalance(0);
      setStakingBalance(0);
      setNFTBalance(0);
      setDexBalance(0);
      setLendingBalance(0);
      setValidatorStakingBalance(0);
      setManagedFundsBalance(0);
      setNFTMarketsBalance(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // TODO Add back when we are have our own Trading Bots
  // const [strategyBalance, setStrategyBalance] = useState(0);

  useEffect(() => {
    setNetworth(
      (userTokenAccountsBalanceUSD ?? 0) +
        liquidityBalance +
        farmBalance +
        marginBalance +
        vaultsBalance +
        NFTBalance +
        dexBalance +
        validatorStakingBalance +
        lendingBalance +
        stakingBalance +
        managedFundsBalance +
        nftMarketsBalance,
      // + strategyBalance
    );
  }, [
    userTokenAccountsBalanceUSD,
    liquidityBalance,
    farmBalance,
    marginBalance,
    vaultsBalance,
    NFTBalance,
    dexBalance,
    validatorStakingBalance,
    lendingBalance,
    stakingBalance,
    managedFundsBalance,
    nftMarketsBalance,
  ]);

  useEffect(() => {
    if (connected && networth > 0) {
      document.title = `${calculatePriceForDisplayCurrency(networth)} ${displayCurrency} | Step`;
    } else {
      document.title = "Step";
    }
  }, [networth, connected, displayCurrency, calculatePriceForDisplayCurrency]);

  const memoizedPortfolioOverviewState = useMemo(
    () => ({
      networth,
      farmBalance,
      NFTBalance,
      marginBalance,
      dexBalance,
      lendingBalance,
      stakingBalance,
      managedFundsBalance,
      walletBalance: userTokenAccountsBalanceUSD ?? 0,
      lpBalance: liquidityBalance,
      yieldAggBalance: vaultsBalance,
      validatorBalance: validatorStakingBalance,
      nftMarketsBalance,
    }),
    [
      networth,
      farmBalance,
      NFTBalance,
      marginBalance,
      dexBalance,
      lendingBalance,
      stakingBalance,
      managedFundsBalance,
      userTokenAccountsBalanceUSD,
      liquidityBalance,
      vaultsBalance,
      validatorStakingBalance,
      nftMarketsBalance,
    ],
  );

  return (
    <PortfolioOverviewContext.Provider value={memoizedPortfolioOverviewState}>
      {children}
    </PortfolioOverviewContext.Provider>
  );
};

export function usePortfolioOverviewContext(): PortfolioOverview {
  const ctx = useContext(PortfolioOverviewContext);
  if (ctx === null) {
    throw new Error("Portfolio Overview Context not available");
  }
  return ctx;
}
